import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { bookingApi } from '../services/api';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface Booking {
  id: string;
  date: string;
  status: string;
  price: number;
  duration: number;
  note?: string;
  service: {
    id: string;
    name: string;
  };
  staff?: {
    id: string;
    name: string;
  };
  customer: {
    name: string;
    email: string;
    phone?: string;
  };
}

interface MyBookingState {
  bookings: Record<string, Booking>; // token => booking
  isLoading: boolean;
  error: string | null;

  fetchBooking: (token: string) => Promise<void>;
  rescheduleBooking: (token: string, data: { date: string; serviceId?: string; staffId?: string }) => Promise<void>;
  cancelBooking: (token: string) => Promise<void>;
  clearBooking: (token: string) => void;
}

export const useMyBookingStore = create<MyBookingState>()(
  persist(
    (set, get) => ({
      bookings: {},
      isLoading: false,
      error: null,

      fetchBooking: async (token: string) => {
        const { bookings } = get();
        const hasExistingData = !!bookings[token];

        // 如果有快取資料，不顯示骨架載入
        set({ isLoading: !hasExistingData, error: null });

        try {
          const response = await bookingApi.getByToken(token);
          set((state) => ({
            bookings: {
              ...state.bookings,
              [token]: response.data,
            },
            isLoading: false,
          }));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.fetchFailed');
          set({ error: errorMsg, isLoading: false });
          toast.error(errorMsg);
        }
      },

      rescheduleBooking: async (token: string, data: { date: string; serviceId?: string; staffId?: string }) => {
        try {
          const response = await bookingApi.rescheduleByToken(token, data);

          // 樂觀更新本地狀態
          set((state) => ({
            bookings: {
              ...state.bookings,
              [token]: response.data,
            },
          }));

          toast.success(i18n.t('common.messages.updateSuccess'));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
          toast.error(errorMsg);
          throw error;
        }
      },

      cancelBooking: async (token: string) => {
        // 1. 保存舊值
        const originalBooking = get().bookings[token];

        // 2. 樂觀更新：立即更新狀態
        set((state) => {
          const booking = state.bookings[token];
          if (booking) {
            return {
              bookings: {
                ...state.bookings,
                [token]: { ...booking, status: 'CANCELLED' },
              },
            };
          }
          return state;
        });
        toast.success(i18n.t('common.messages.deleteSuccess'));

        try {
          await bookingApi.cancelByToken(token);
        } catch (error: any) {
          // 3. 失敗回滾
          if (originalBooking) {
            set((state) => ({
              bookings: { ...state.bookings, [token]: originalBooking },
            }));
          }
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.deleteFailed');
          toast.error(errorMsg);
          throw error;
        }
      },

      clearBooking: (token: string) => {
        set((state) => {
          const { [token]: removed, ...rest } = state.bookings;
          return { bookings: rest };
        });
      },
    }),
    {
      name: 'my-booking-storage',
      partialize: (state) => ({
        bookings: state.bookings,
      }),
    }
  )
);
